import React from "react";
import PropTypes from "prop-types";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import classnames from "classnames";
import _throttle from "lodash/throttle";
import withCustomStyles from "./SlidersListPart.style";
import { getAbsUrl, getUrlPath } from "../utils/UrlUtils";
import gtmService from "../services/GTMService";
import { getStandardAnalyticsSlider } from "../utils/SliderUtils";
import { GRail } from "./ui";
import { scrollBehavior, prefersReducedMotion } from "../utils/HelperUtils";

export const sliderLink = slider => {
  if (
    slider.landing_page &&
    slider.landing_page.startsWith("https://grocerapp.pk")
  ) {
    return getUrlPath(slider.landing_page);
  }
  return slider.link_type === "category"
    ? `cn/category/cid/${slider.link_id}`
    : "#";
};

const Chevron = ({ dir }) => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.4"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d={dir === "left" ? "m15 18-6-6 6-6" : "m9 18 6-6-6-6"} />
  </svg>
);

// Promotions rail: scroll-snap row of banner images that auto-advances every
// `autoplayInterval` ms (wrapping back to the first). Autoplay pauses while
// the rail is hovered/focused or the tab is hidden, restarts its countdown
// after any manual interaction, and is off for prefers-reduced-motion users.
// The next banner peeks in from the right, and overlay arrows + dots signal
// that the row scrolls.
class SlidersListPart extends React.Component {
  scrollerRef = React.createRef();

  state = { positions: [0], active: 0, canPrev: false, canNext: false };

  paused = false;

  autoplayTimer = null;

  componentDidMount() {
    const el = this.scrollerRef.current;
    if (el) el.addEventListener("scroll", this.update, { passive: true });
    window.addEventListener("resize", this.update);
    document.addEventListener("visibilitychange", this.scheduleAutoplay);
    this.update();
    this.scheduleAutoplay();
  }

  componentDidUpdate(prevProps) {
    if (prevProps.sliders !== this.props.sliders) this.update();
  }

  componentWillUnmount() {
    const el = this.scrollerRef.current;
    if (el) el.removeEventListener("scroll", this.update);
    window.removeEventListener("resize", this.update);
    document.removeEventListener("visibilitychange", this.scheduleAutoplay);
    this.update.cancel();
    clearTimeout(this.autoplayTimer);
  }

  // (Re)starts the autoplay countdown; called after every tick and every
  // user interaction so a manual change always gets a full interval.
  scheduleAutoplay = () => {
    clearTimeout(this.autoplayTimer);
    const { autoplayInterval } = this.props;
    if (!autoplayInterval || this.paused || document.hidden) return;
    if (prefersReducedMotion()) return;
    this.autoplayTimer = setTimeout(this.autoplayTick, autoplayInterval);
  };

  autoplayTick = () => {
    const { positions, active } = this.state;
    if (positions.length > 1) {
      this.goTo(active >= positions.length - 1 ? 0 : active + 1);
    }
    this.scheduleAutoplay();
  };

  pauseAutoplay = () => {
    this.paused = true;
    clearTimeout(this.autoplayTimer);
  };

  resumeAutoplay = () => {
    this.paused = false;
    this.scheduleAutoplay();
  };

  handleFocusOut = e => {
    if (!e.currentTarget.contains(e.relatedTarget)) this.resumeAutoplay();
  };

  // Snap positions = each banner's left edge, clamped to the max scroll.
  getPositions = el => {
    const max = el.scrollWidth - el.clientWidth;
    const items = Array.from(el.children);
    if (max <= 1 || items.length === 0) return [0];
    const origin = items[0].offsetLeft;
    const positions = [];
    items.forEach(item => {
      const left = Math.min(item.offsetLeft - origin, max);
      if (!positions.length || left - positions[positions.length - 1] > 2) {
        positions.push(left);
      }
    });
    return positions;
  };

  update = _throttle(
    () => {
      const el = this.scrollerRef.current;
      if (!el) return;
      const positions = this.getPositions(el);
      const x = el.scrollLeft;
      let active = 0;
      positions.forEach((p, i) => {
        if (Math.abs(p - x) < Math.abs(positions[active] - x)) active = i;
      });
      const max = el.scrollWidth - el.clientWidth;
      this.setState({
        positions,
        active,
        canPrev: x > 2,
        canNext: x < max - 2
      });
    },
    100,
    { leading: true, trailing: true }
  );

  goTo = index => {
    const el = this.scrollerRef.current;
    const { positions } = this.state;
    if (!el || !positions.length) return;
    const i = Math.max(0, Math.min(index, positions.length - 1));
    el.scrollTo({ left: positions[i], behavior: scrollBehavior() });
  };

  // Manual navigation (arrows/dots) restarts the autoplay countdown.
  navigate = index => {
    this.goTo(index);
    this.scheduleAutoplay();
  };

  render() {
    const { sliders, classes, kind } = this.props;
    const { positions, active, canPrev, canNext } = this.state;
    const scrollable = positions.length > 1;

    return (
      <div
        className={classes.root}
        onMouseEnter={this.pauseAutoplay}
        onMouseLeave={this.resumeAutoplay}
        onFocus={this.pauseAutoplay}
        onBlur={this.handleFocusOut}
        onTouchStart={this.scheduleAutoplay}
      >
        <div className={classes.stage}>
          <GRail
            kind={kind}
            ariaLabel="Promotions"
            scrollerRef={this.scrollerRef}
            rowClassName={sliders.length > 2 ? classes.peek : undefined}
          >
            {sliders.map((slider, index) => (
              <Link
                key={slider.id}
                to={sliderLink(slider)}
                className={classes.banner}
                onClick={() =>
                  gtmService.pageView(
                    "/slider-click",
                    getStandardAnalyticsSlider(
                      slider,
                      getAbsUrl(sliderLink(slider))
                    )
                  )
                }
              >
                <img
                  width="1025"
                  height="341"
                  src={index < 2 ? slider.image_url : undefined}
                  data-src={index < 2 ? undefined : slider.image_url}
                  alt={slider.title || slider.name || "Promotion"}
                  className={classnames(classes.image, {
                    lazyload: index >= 2
                  })}
                />
              </Link>
            ))}
          </GRail>
          {canPrev && (
            <button
              type="button"
              className={classnames(classes.nav, classes.navPrev)}
              onClick={() => this.navigate(active - 1)}
              aria-label="Previous promotions"
            >
              <Chevron dir="left" />
            </button>
          )}
          {canNext && (
            <button
              type="button"
              className={classnames(classes.nav, classes.navNext)}
              onClick={() => this.navigate(active + 1)}
              aria-label="More promotions"
            >
              <Chevron dir="right" />
            </button>
          )}
        </div>
        {scrollable && (
          <div className={classes.dots}>
            {positions.map((p, i) => (
              <button
                key={p}
                type="button"
                className={classnames(classes.dot, {
                  [classes.dotActive]: i === active
                })}
                onClick={() => this.navigate(i)}
                aria-label={`Show promotion ${i + 1} of ${positions.length}`}
                aria-current={i === active ? "true" : undefined}
              />
            ))}
          </div>
        )}
      </div>
    );
  }
}

SlidersListPart.propTypes = {
  sliders: PropTypes.array.isRequired,
  kind: PropTypes.oneOf(["banner", "bannerWide"]),
  // Auto-advance delay in ms; 0 disables autoplay.
  autoplayInterval: PropTypes.number
};

SlidersListPart.defaultProps = {
  kind: "bannerWide",
  autoplayInterval: 4000
};

export default withRouter(withCustomStyles(SlidersListPart));
